import React from 'react'
import { Calendar, Star, MessageCircle, BookOpen, Clock } from 'lucide-react'

// Updated: Accept onSubmit, onReset, and initialFormData (if needed for external reset)
const FollowupForm = ({
  formData,
  setFormData,
  onSubmit, // New prop for handling form submission in the parent
  onReset, // New prop for handling form reset in the parent
  recordId, // Assuming record_id comes from a parent context
  isSubmitting: parentIsSubmitting, // Allow parent to control global submission state if needed
}) => {
  const [errors, setErrors] = React.useState({})
  // Use a local isSubmitting state for button UI, but can be overridden/combined with parent's
  const [localIsSubmitting, setLocalIsSubmitting] = React.useState(false)

  // Determine the effective submission state
  const effectiveIsSubmitting = parentIsSubmitting || localIsSubmitting

  React.useEffect(() => {
    // If the recordId changes, or it's initially set, update formData
    if (recordId !== undefined && formData.record_id !== recordId) {
      setFormData((prev) => ({
        ...prev,
        record_id: recordId,
      }))
    }
  }, [recordId, formData.record_id, setFormData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleRatingChange = (rating) => {
    setFormData((prev) => ({
      ...prev,
      response_rating: rating,
    }))

    if (errors.response_rating) {
      setErrors((prev) => ({
        ...prev,
        response_rating: '',
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // record_id is now assumed to be passed from parent, so we validate its presence there
    // but we can still check if it's somehow missing in formData if needed for robustness
    if (!formData.record_id || formData.record_id === '') {
      newErrors.record_id = 'Record ID is required (internal form check)'
    }

    if (!formData.user_response.trim()) {
      newErrors.user_response = 'User response is required'
    }

    // if (!formData.next_followup_date) {
    //   newErrors.next_followup_date = 'Next followup date is required'
    // }

    // if (!formData.interested_course.trim()) {
    //   newErrors.interested_course = 'Interested course is required'
    // }

    if (formData.response_rating === 0) {
      newErrors.response_rating = 'Please provide a rating'
    }

    if (!formData.call_at) {
      newErrors.call_at = 'Call date and time is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmitInternal = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    setLocalIsSubmitting(true) // Start local loading state

    try {
      // Call the onSubmit prop function, passing the current formData
      await onSubmit(formData)
      setErrors({}) // Clear errors on successful submission
    } catch (error) {
      // The parent's onSubmit should handle the actual error logging/display for API errors
      console.error('Form submission failed in parent:', error)
    } finally {
      setLocalIsSubmitting(false) // End local loading state
    }
  }

  const handleResetInternal = () => {
    setErrors({}) // Clear internal errors
    if (onReset) {
      onReset() // Call parent's reset handler
    }
  }

  return (
    <div className='max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen'>
      <div className='bg-white rounded-lg shadow-lg p-8'>
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-800 mb-2'>
            Create Followup
          </h1>
          <p className='text-gray-600'>
            Fill in the details for the followup record
          </p>
        </div>

        <form onSubmit={handleSubmitInternal}>
          <div className='space-y-6'>
            {/* User Response */}
            <div>
              <label
                htmlFor='user_response'
                className='flex items-center text-sm font-medium text-gray-700 mb-2'
              >
                <MessageCircle className='w-4 h-4 mr-2 text-green-500' />
                User Response *
              </label>
              <textarea
                id='user_response'
                name='user_response'
                value={formData.user_response}
                onChange={handleInputChange}
                rows='4'
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-vertical ${
                  errors.user_response
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300'
                }`}
                placeholder="Enter user's response..."
                disabled={effectiveIsSubmitting}
              />
              {errors.user_response && (
                <p className='text-red-500 text-sm mt-1'>
                  {errors.user_response}
                </p>
              )}
            </div>

            {/* Date and Time Row */}
            <div className='grid md:grid-cols-2 gap-6'>
              {/* Next Followup Date */}
              <div>
                <label
                  htmlFor='next_followup_date'
                  className='flex items-center text-sm font-medium text-gray-700 mb-2'
                >
                  <Calendar className='w-4 h-4 mr-2 text-purple-500' />
                  Next Followup Date
                </label>
                <input
                  id='next_followup_date'
                  type='date'
                  name='next_followup_date'
                  value={formData.next_followup_date}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.next_followup_date
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  disabled={effectiveIsSubmitting}
                />
                {errors.next_followup_date && (
                  <p className='text-red-500 text-sm mt-1'>
                    {errors.next_followup_date}
                  </p>
                )}
              </div>

              {/* Call At */}
              <div>
                <label
                  htmlFor='call_at'
                  className='flex items-center text-sm font-medium text-gray-700 mb-2'
                >
                  <Clock className='w-4 h-4 mr-2 text-orange-500' />
                  Call Date & Time *
                </label>
                <input
                  id='call_at'
                  type='datetime-local'
                  name='call_at'
                  value={formData.call_at}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.call_at
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  disabled={effectiveIsSubmitting}
                />
                {errors.call_at && (
                  <p className='text-red-500 text-sm mt-1'>{errors.call_at}</p>
                )}
              </div>
            </div>

            {/* Interested Course (Now an input field) */}
            <div>
              <label
                htmlFor='interested_course'
                className='flex items-center text-sm font-medium text-gray-700 mb-2'
              >
                <BookOpen className='w-4 h-4 mr-2 text-indigo-500' />
                Interested Course
              </label>
              <input
                id='interested_course'
                type='text'
                name='interested_course'
                value={formData.interested_course}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.interested_course
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-300'
                }`}
                placeholder='e.g., Full Stack Web Development'
                disabled={effectiveIsSubmitting}
              />
              {errors.interested_course && (
                <p className='text-red-500 text-sm mt-1'>
                  {errors.interested_course}
                </p>
              )}
            </div>

            {/* Response Rating */}
            <div>
              <label className='flex items-center text-sm font-medium text-gray-700 mb-3'>
                <Star className='w-4 h-4 mr-2 text-yellow-500' />
                Response Rating *
              </label>
              <div className='flex items-center space-x-2'>
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type='button'
                    onClick={() => handleRatingChange(rating)}
                    disabled={effectiveIsSubmitting}
                    className={`p-2 rounded-lg transition-all transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed ${
                      formData.response_rating >= rating
                        ? 'text-yellow-500 bg-yellow-50'
                        : 'text-gray-300 hover:text-yellow-400'
                    }`}
                  >
                    <Star
                      className={`w-8 h-8 ${
                        formData.response_rating >= rating ? 'fill-current' : ''
                      }`}
                    />
                  </button>
                ))}
                <span className='ml-3 text-sm text-gray-600'>
                  {formData.response_rating > 0
                    ? `${formData.response_rating}/5`
                    : 'No rating'}
                </span>
              </div>
              {errors.response_rating && (
                <p className='text-red-500 text-sm mt-1'>
                  {errors.response_rating}
                </p>
              )}
            </div>

            {/* Remark */}
            <div>
              <label
                htmlFor='remark'
                className='flex items-center text-sm font-medium text-gray-700 mb-2'
              >
                <MessageCircle className='w-4 h-4 mr-2 text-gray-500' />
                Remark (Optional)
              </label>
              <textarea
                id='remark'
                name='remark'
                value={formData.remark}
                onChange={handleInputChange}
                rows='3'
                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-vertical'
                placeholder='Add any additional remarks...'
                disabled={effectiveIsSubmitting}
              />
            </div>

            {/* Action Buttons */}
            <div className='flex flex-col sm:flex-row gap-4 pt-6 border-t'>
              <button
                type='submit' // Changed to type='submit'
                disabled={effectiveIsSubmitting}
                className={`flex-1 font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-105 shadow-lg ${
                  effectiveIsSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                }`}
              >
                {effectiveIsSubmitting ? 'Submitting...' : 'Create Followup'}
              </button>
              <button
                type='button' // Changed to type='button' to prevent form submission
                onClick={handleResetInternal}
                disabled={effectiveIsSubmitting}
                className={`flex-1 font-semibold py-3 px-6 rounded-lg transition-all transform hover:scale-105 ${
                  effectiveIsSubmitting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                }`}
              >
                Reset Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FollowupForm
